import { formatDurationLimits } from "./duration";
import type { DurationLimits } from "./duration";
import type { RaffleField, RaffleFormValues } from "./formValues";

export type RaffleErrors = Partial<Record<RaffleField, string>>;

const REQUIRED = "This field is required.";
const USDT_PATTERN = /^\d+(\.\d{1,2})?$/;
const INTEGER_PATTERN = /^\d+$/;
const INVITE_CODE_MAX = 64;
const MIN_PRIZE = 0.01;

const validateUsdt = (value: string): string | undefined => {
  if (!value.trim()) return REQUIRED;
  if (!USDT_PATTERN.test(value.trim())) {
    return "Enter an amount with up to 2 decimals.";
  }
  if (Number(value) <= 0) return "Amount must be greater than 0.";
  return undefined;
};

const validateWinners = (value: string): string | undefined => {
  if (!value.trim()) return REQUIRED;
  if (!INTEGER_PATTERN.test(value.trim()) || Number(value) < 1) {
    return "Enter a whole number, at least 1.";
  }
  return undefined;
};

// Пул делится поровну — каждому победителю должно достаться хотя бы 0.01.
const validatePrizePool = (values: RaffleFormValues, balance: number) => {
  const error = validateUsdt(values.prizePool);
  if (error) return error;
  const pool = Number(values.prizePool);
  if (pool > balance) return "Not enough real balance.";
  if (!validateWinners(values.winnersCount)) {
    const perWinner = pool / Number(values.winnersCount);
    if (perWinner < MIN_PRIZE) {
      return "Pool is too small for this many winners.";
    }
  }
  return undefined;
};

const validateDuration = (total: number, limits: DurationLimits) =>
  total < limits.min_seconds || total > limits.max_seconds
    ? `Duration must be ${formatDurationLimits(limits)}.`
    : undefined;

const validateInviteCode = ({ visibility, inviteCode }: RaffleFormValues) => {
  if (visibility !== "private") return undefined;
  if (!inviteCode.trim()) return REQUIRED;
  if (inviteCode.trim().length > INVITE_CODE_MAX) {
    return `Up to ${INVITE_CODE_MAX} characters.`;
  }
  return undefined;
};

export const validateRaffle = (
  values: RaffleFormValues,
  balance: number,
  limits: DurationLimits,
): RaffleErrors => ({
  prizePool: validatePrizePool(values, balance),
  winnersCount: validateWinners(values.winnersCount),
  durationSec: validateDuration(values.durationSec, limits),
  symbols: values.symbols.length ? undefined : "Select at least one pair.",
  volume: validateUsdt(values.volume),
  minTier: values.minTier ? undefined : "Select a status.",
  inviteCode: validateInviteCode(values),
});

export const hasRaffleErrors = (errors: RaffleErrors): boolean =>
  Object.values(errors).some(Boolean);
